import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigation } from 'react-router';
import type { ShopperExperience, ShopperSearch } from '@/scapi';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import CategorySorting from '@/components/category-sorting';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { Region } from '@/components/region';
import { SeoMeta } from '@/components/seo-meta';
import { getSearchWithoutFiltersParam, useFiltersPanelState } from '@/hooks/use-filters-panel-state';
import { ProductListing } from '@/features/product-listing';
import { useLoadMoreProducts } from '@/hooks/use-load-more-products';
import { uiConfig } from '@/lib/config.ui';
import { getProductListingUrl } from '@/extensions/snk/lib/product-listing-url';

type PageWithDesignMetadata = ShopperExperience.schemas['Page'] & {
    componentData?: Record<string, Promise<unknown>>;
};

const EMPTY_PRODUCTS = Promise.resolve([] as ShopperSearch.schemas['ProductSearchHit'][]);

export type SearchPageData = {
    searchTerm: string;
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    deferredProducts?: Promise<ShopperSearch.schemas['ProductSearchHit'][]>;
    page: Promise<PageWithDesignMetadata | null>;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    initialCount?: number;
};

export function SearchPage({
    loaderData: {
        searchTerm,
        searchResultCritical,
        deferredProducts = EMPTY_PRODUCTS,
        page,
        pageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
        initialCount: initialCountFromLoader,
    },
}: {
    loaderData: SearchPageData;
}) {
    const { t } = useTranslation('search');
    const config = useConfig();
    const limit = config.search.products.hits.limit;

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);

    const analytics = useAnalytics();
    const lastTrackedSearchRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const paginationConfig = uiConfig.pages.category.pagination;
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const initialCount = initialCountFromLoader ?? criticalCount;
    const deferredInitialCount = Math.max(0, initialCount - criticalCount);
    const {
        appended,
        loadedCount,
        total,
        hasMore,
        isLoading,
        isRestoring,
        restorationTarget,
        firstNewIndex,
        loadMore,
    } = useLoadMoreProducts({
        query: searchTerm,
        refine,
        sort: searchResultCritical.selectedSortingOption,
        currency,
        initialCount,
        total: searchResultCritical.total,
        batchSize: paginationConfig.batchSize,
        mobileBatchSize: paginationConfig.mobileBatchSize,
        maxProducts: paginationConfig.maxProducts,
        identity: `${searchTerm}-${pageIdentity}-${searchWithoutFiltersParam}`,
        offset: searchResultCritical.offset,
    });

    const firstNewItemRef = useRef<HTMLElement>(null);
    const focusTargetKey = firstNewIndex === null ? null : `${appended.length}-${firstNewIndex}`;
    useEffect(() => {
        if (focusTargetKey === null || !firstNewItemRef.current) return;
        const node = firstNewItemRef.current;
        node.setAttribute('tabindex', '-1');
        node.focus({ preventScroll: true });
        const onBlur = () => node.removeAttribute('tabindex');
        node.addEventListener('blur', onBlur, { once: true });
        return () => node.removeEventListener('blur', onBlur);
    }, [focusTargetKey]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInSearch({
                    searchInputText: searchTerm,
                    product,
                });
            }
        },
        [analytics, searchTerm]
    );

    useEffect(() => {
        // Only track if we haven't already tracked this search
        if (analytics && analyticsKey !== lastTrackedSearchRef.current) {
            lastTrackedSearchRef.current = analyticsKey;

            void deferredProducts
                .then((products) =>
                    analytics.trackViewSearch({
                        searchInputText: searchTerm,
                        searchResults: [...(searchResultCritical.hits ?? []), ...products],
                        sort:
                            searchResultCritical.selectedSortingOption ||
                            searchResultCritical.sortingOptions?.[0]?.label ||
                            '',
                        refinements: searchResultCritical.selectedRefinements ?? {},
                        offset: searchResultCritical.offset,
                        limit,
                        total: searchResultCritical.total,
                    })
                )
                .catch(() => {
                    // Analytics must not affect the product-listing experience.
                });
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, searchTerm, analyticsKey, searchResultCritical, deferredProducts]);

    return (
        <>
            <SeoMeta
                title={
                    searchTerm
                        ? t('titleWithQuery', { query: searchTerm, defaultValue: `Results for "${searchTerm}"` })
                        : t('title', { defaultValue: 'Search' })
                }
                description={
                    searchTerm
                        ? t('meta.descriptionWithQuery', {
                              query: searchTerm,
                              count: searchResultCritical.total,
                              defaultValue: `${searchResultCritical.total} results for "${searchTerm}"`,
                          })
                        : t('meta.description', { defaultValue: 'Search our store for products' })
                }
                openGraph={{ type: 'website', url: pageUrl }}
            />
            <div className="pb-16">
                <div className="section-container">
                    <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <p>{t('results')}</p>
                            <h1 className="text-3xl font-bold leading-none tracking-[-0.75px] text-card-foreground">
                                {searchTerm} ({searchResultCritical.total})
                            </h1>
                        </div>
                        {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                            <div className="flex-shrink-0">
                                <CategorySorting result={searchResultCritical} />
                            </div>
                        )}
                    </div>

                    {/* searchTopFullWidth */}
                    <Region className="mb-8" page={page} regionId="searchTopFullWidth" />

                    <ProductListing
                        productGridDataKey={productGridDataKey}
                        result={searchResultCritical}
                        refine={refine}
                        filtersOpen={filtersOpen}
                        onToggleFiltersOpen={toggleFiltersOpen}
                        selectedFiltersCount={selectedFiltersCount}
                        isProductGridLoading={isProductGridLoading}
                        limit={limit}
                        handleProductClick={handleProductClick}
                        getProductUrl={getProductListingUrl}
                        appended={appended}
                        firstNewIndex={firstNewIndex}
                        firstNewItemRef={firstNewItemRef}
                        appendPending={isLoading}
                        isRestoring={isRestoring}
                        restorationTarget={restorationTarget}
                        deferredProducts={deferredProducts}
                        deferredSkeletonCount={deferredInitialCount}
                        loadMore={{ loadedCount, total, hasMore, isLoading, onLoadMore: loadMore }}
                        wrapperClassName="gap-8">
                        <Region className="mb-8" page={page} regionId="searchTopContent" />
                    </ProductListing>
                    <Region className="mt-8" page={page} regionId="searchBottom" />
                </div>
            </div>
        </>
    );
}

export default SearchPage;
